import type { Item } from '../dto/searchResultHit';
import { getFrameDurationFromFrameRateApproximation } from '../util/framerate';
import type { FuzzyValue } from './fuzzyNumbers';
import { getTechnicalMetadataValue, TechnicalVideoDataField } from '../util/technicalMetadata';
import type { PlayableArtifactSelectOptions, AvailableArtifact } from '../dto/playableItemDto';
import type { CodecString, MediaAvailability } from '../dto/mediaAvailability';
import { StreamRoleId } from '../dto/mediaAvailability';
import type { StreamSetPreference } from '../dto/mediaDtos';
import type { TamsReference } from '../dto/tams';
import type { TechnicalMetadata } from '../dto/fieldValue';
import type { HighresDeletion } from '../dto/itemPropTypes';
import { wasHighresDeleted } from './item';

export const DEFAULT_FRAMERATE = 30;

export function getMediaFrameRateFromItem(item: Pick<Item, 'mediaFrameRate' | 'technicalMetadata'>) {
  return item.mediaFrameRate || getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalVideoDataField.FrameRate) || null;
}


export function getFrameDuration(item: Pick<Item, 'mediaFrameRate' | 'technicalMetadata'>): FuzzyValue {
  const mediaFrameRate = getMediaFrameRateFromItem(item);
  if (mediaFrameRate == null) {
    return { approximate: 1 / DEFAULT_FRAMERATE, accuracyWarning: 'Frame rate unknown' };
  }

  return getFrameDurationFromFrameRateApproximation(mediaFrameRate);
}


function matchesStreamSet(streamSet: StreamRoleId[], codecsByStreamId: Partial<Record<StreamRoleId, CodecString>>) {
  for (const streamId of streamSet) {
    if (codecsByStreamId[streamId]) {
      return true;
    }
  }
  return false;
}

function selectStreamSet(
  streamSetPreference: StreamSetPreference,
  codecsByStreamId: Partial<Record<StreamRoleId, CodecString>> | undefined,
): StreamRoleId[] | null {
  return streamSetPreference.find(ss => matchesStreamSet(ss, codecsByStreamId ?? { })) || null;
}


/**
 * We only look at the file extension to decide that it may be playable.
 * In reality, it also depends on the codecs etc to actually play in the browser,
 * But most of the common codecs in these containers are supported, and therefore
 * we skip more complicated checks to keep it simple for common scenarios.
 */
export function fileMayBePlayable(originalFileName: string | undefined | null): boolean {
  const possiblyPlayableHighResExtensions = ['.mp4', '.webm', '.ogg'];
  if (!originalFileName) {
    return false;
  }

  const extensionStart = originalFileName.lastIndexOf('.');
  if (extensionStart === -1) {
    return false;
  }
  const ext = originalFileName.slice(extensionStart).toLowerCase();
  if (!possiblyPlayableHighResExtensions.includes(ext)) {
    return false;
  }

  return true;
}


function isEditProxyBrowserPlayableAndAvailable(
  editProxyFormat: string | null,
  editProxyUrl: string | null,
) {
  return editProxyFormat !== 'prores' && editProxyUrl;
}

const MEDIA_DELETED_REASON = 'Media deleted';
const INTERLACED_WARNING = 'Media is interlaced, browser playback might not work properly';
const FORMAT_UNSUPPORTED_REASON = 'Format not supported for browser playback';

function isInterlacedHighres(technicalMetadata: TechnicalMetadata | undefined): boolean {
  const fieldOrder = getTechnicalMetadataValue<string>(technicalMetadata, TechnicalVideoDataField.FieldOrder);
  if (fieldOrder === null) {
    return false;
  }
  return fieldOrder !== 'progressive';
}

/**
 * Returns all available artifacts with browser playability status.
 * Returns artifacts in priority order.
 */
export function getAllArtifacts(
  item: {
    media: MediaAvailability | null,
    editProxyUrl: string | null,
    editProxyFormat: string | null,
    proxyUrl: string | null,
    highresUrl: string | null,
    originalFileName: string | null,
    tamsReference: TamsReference | undefined,
    highresDeletion?: HighresDeletion,
    technicalMetadata: TechnicalMetadata | undefined,
  },
  options: PlayableArtifactSelectOptions,
): AvailableArtifact[] {
  const artifacts: AvailableArtifact[] = [];
  const highresDeleted = wasHighresDeleted(item);

  // item.media will be present for segmented media. It used to mean live items
  // but now this may also happen for other ingests workflows that produce ts
  // segmented proxies, like for instance Kelda watchfolders
  if (item.media) {
    const codecsByStreamId = item.media.codecsByStreamId;
    const preferredStreamSet = selectStreamSet(options.streamSetPreference, codecsByStreamId);
    const addedStreamIds = new Set<StreamRoleId>();

    if (preferredStreamSet) {
      const containsHighres = preferredStreamSet.includes(StreamRoleId.LiveRecordHighresTs);
      if (highresDeleted && containsHighres) {
        artifacts.push({
          type: 'stream',
          streamSet: preferredStreamSet,
          playableStatus: 'unplayable',
          reason: MEDIA_DELETED_REASON,
        });
      } else {
        const warning = containsHighres && isInterlacedHighres(item.technicalMetadata)
          ? INTERLACED_WARNING
          : undefined;
        artifacts.push({
          type: 'stream',
          streamSet: preferredStreamSet,
          playableStatus: 'playable',
          warning,
        });
      }
      for (const streamId of preferredStreamSet) {
        addedStreamIds.add(streamId);
      }
    }

    for (const streamId of Object.keys(codecsByStreamId) as StreamRoleId[]) {
      if (!addedStreamIds.has(streamId)) {
        const isHighresStream = streamId === StreamRoleId.LiveRecordHighresTs;
        if (highresDeleted && isHighresStream) {
          artifacts.push({
            type: 'stream',
            streamSet: [streamId],
            playableStatus: 'unplayable',
            reason: MEDIA_DELETED_REASON,
          });
        } else {
          const warning = isHighresStream && isInterlacedHighres(item.technicalMetadata)
            ? INTERLACED_WARNING
            : undefined;
          artifacts.push({
            type: 'stream',
            streamSet: [streamId],
            playableStatus: 'playable',
            warning,
          });
        }
      }
    }
  }

  if (item.editProxyUrl && options.useEditProxy) {
    const isPlayable = isEditProxyBrowserPlayableAndAvailable(item.editProxyFormat, item.editProxyUrl);
    if (isPlayable) {
      artifacts.push({
        type: 'flatEditProxy',
        url: item.editProxyUrl,
        playableStatus: 'playable',
      });
    } else {
      artifacts.push({
        type: 'flatEditProxy',
        url: item.editProxyUrl,
        playableStatus: 'unplayable',
        reason: FORMAT_UNSUPPORTED_REASON,
      });
    }
  }

  if (item.proxyUrl) {
    artifacts.push({
      type: 'flatWebProxy',
      url: item.proxyUrl,
      playableStatus: 'playable',
    });
  }

  if (item.editProxyUrl) {
    const isPlayable = isEditProxyBrowserPlayableAndAvailable(item.editProxyFormat, item.editProxyUrl);
    if (isPlayable) {
      artifacts.push({
        type: 'flatEditProxy',
        url: item.editProxyUrl,
        playableStatus: 'playable',
      });
    } else {
      artifacts.push({
        type: 'flatEditProxy',
        url: item.editProxyUrl,
        playableStatus: 'unplayable',
        reason: FORMAT_UNSUPPORTED_REASON,
      });
    }
  }

  if (item.highresUrl) {
    if (highresDeleted) {
      artifacts.push({
        type: 'flatHighRes',
        url: item.highresUrl,
        playableStatus: 'unplayable',
        reason: MEDIA_DELETED_REASON,
      });
    } else {
      const isPlayable = fileMayBePlayable(item.originalFileName);
      if (isPlayable) {
        artifacts.push({
          type: 'flatHighRes',
          url: item.highresUrl,
          playableStatus: 'playable',
        });
      } else {
        artifacts.push({
          type: 'flatHighRes',
          url: item.highresUrl,
          playableStatus: 'unplayable',
          reason: FORMAT_UNSUPPORTED_REASON,
        });
      }
    }
  }

  if (item.tamsReference) {
    if ((item.tamsReference.type === 'flow') && (item.tamsReference.proxyFlowIds !== undefined)) {
      artifacts.push({
        type: 'tams',
        flowIds: item.tamsReference.proxyFlowIds,
        storageId: item.tamsReference.storageId,
        playableStatus: 'playable',
      });
    }
    // TODO: We could use the TAMS client to resolve flows tagged with
    // "mimir-proxy" associated with the source in case the reference
    // is of type "source".
  }

  return artifacts;
}

